export interface GeneratedDeliveryNoteLike {
  delivery_note_no?: string | null;
  delivery_note_id?: number | string | null;
  id?: number | string | null;
  note_no?: string | null;
  pre_delivery_note_id?: number | string | null;
  pre_delivery_note_no?: string | null;
  [key: string]: unknown;
}

export interface DeliveryPalletDraftItem {
  materialNo?: string | null;
  quantity?: number | string | null;
}

export interface DeliveryNotePalletItemLike {
  description?: string | null;
  material_no?: string | null;
  materialNo?: string | null;
  quantity?: number | string | null;
  [key: string]: unknown;
}

export interface DeliveryNotePalletDisplayItem {
  description: string;
  materialNo: string;
  quantity: number;
}

export interface DeliveryPalletBindPayload {
  pallet_items: Array<{
    material_no: string;
    quantity: number;
  }>;
}

function normalizeText(value: unknown) {
  return String(value ?? '').trim();
}

function normalizeComparableCode(value: unknown) {
  return normalizeText(value).toUpperCase();
}

export function getGeneratedDeliveryNoteNo(note?: GeneratedDeliveryNoteLike | null) {
  return normalizeText(note?.note_no || note?.delivery_note_no || note?.pre_delivery_note_no);
}

export function getGeneratedDeliveryNoteBindId(note?: GeneratedDeliveryNoteLike | null) {
  return note?.delivery_note_id || note?.pre_delivery_note_id || note?.id || '';
}

export function extractDeliveryNotePalletItems(note?: Record<string, unknown> | null): DeliveryNotePalletDisplayItem[] {
  const rawItems = note?.pallet_items || note?.palletItems || note?.pallets;
  if (!Array.isArray(rawItems)) {
    return [];
  }

  return (rawItems as DeliveryNotePalletItemLike[]).flatMap((item) => {
    const materialNo = normalizeText(item.material_no || item.materialNo);
    const quantity = Number(item.quantity);
    if (!materialNo || !Number.isFinite(quantity) || quantity <= 0) {
      return [];
    }

    return [{
      description: normalizeText(item.description),
      materialNo,
      quantity,
    }];
  });
}

export function extractGeneratedDeliveryNotes(data: unknown): GeneratedDeliveryNoteLike[] {
  if (Array.isArray(data)) {
    return data as GeneratedDeliveryNoteLike[];
  }

  if (!data || typeof data !== 'object') {
    return [];
  }

  const record = data as {
    data?: unknown;
    items?: unknown;
    list?: unknown;
  };

  if (Array.isArray(record.items)) {
    return record.items as GeneratedDeliveryNoteLike[];
  }
  if (Array.isArray(record.list)) {
    return record.list as GeneratedDeliveryNoteLike[];
  }
  if (Array.isArray(record.data)) {
    return record.data as GeneratedDeliveryNoteLike[];
  }

  return [];
}

export function findGeneratedDeliveryNoteByScanCode(data: unknown, scanCode: string) {
  const normalizedScanCode = normalizeComparableCode(scanCode);
  if (!normalizedScanCode) {
    return null;
  }

  return extractGeneratedDeliveryNotes(data).find((note) => (
    normalizeComparableCode(note.note_no) === normalizedScanCode
    || normalizeComparableCode(note.delivery_note_no) === normalizedScanCode
    || normalizeComparableCode(note.pre_delivery_note_no) === normalizedScanCode
  )) || null;
}

export function buildDeliveryPalletBindPayload(items: DeliveryPalletDraftItem[]): DeliveryPalletBindPayload {
  const quantityByMaterial = new Map<string, number>();

  items.forEach((item) => {
    const materialNo = normalizeText(item.materialNo);
    const quantity = Number(item.quantity);
    if (!materialNo || !Number.isFinite(quantity) || quantity <= 0) {
      return;
    }
    quantityByMaterial.set(materialNo, (quantityByMaterial.get(materialNo) || 0) + quantity);
  });

  return {
    pallet_items: Array.from(quantityByMaterial.entries()).map(([materialNo, quantity]) => ({
      material_no: materialNo,
      quantity,
    })),
  };
}
